import { Button, Stack, Typography } from "@mui/material";
import CustomTextInput from "components/atoms/CustomTextInput";
import { useFormik } from "formik";
import React, { useMemo } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { useCustomMutation } from "store/hooks/react-query/mutate/useMutateFunc";
import { handleErrorProps } from "utils/handleErrorProps";
import { GET_HMOS } from "utils/reactQueryKeys";

const DeleteModal = ({ hmo, toggleModal }) => {
  const queryClient = useQueryClient();

  //Edit Hmo
  const { mutate, isLoading } = useCustomMutation(
    {
      url: `/hmo/${hmo?.id}`,
      method: "delete",
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(GET_HMOS);
        toast.success("Success");
        toggleModal();
      },

      onError: (error) => toast.error(error.message),
    }
  );

  const listResetStyles = {
    listStyle: "initial",
    margin: "initial",
    padding: "2px 14px",
  };
  return (
    <Stack spacing={4}>
      <Typography sx={{ fontWeight: 800, fontSize: "21px" }}>
        Are You Sure You Want to Delete This HMO Provider?
      </Typography>
      <Stack spacing={1}>
        <Typography sx={{ fontWeight: 400, fontSize: "14px" }}>
          You are about to delete <strong>{hmo?.name}</strong> from the
          hospital's database. This action is permanent and cannot be undone.
          Deleting this provider will:
        </Typography>
        <ul style={listResetStyles}>
          <li style={{ ...listResetStyles, padding: "2px 5px" }}>
            Remove their details from the system.
          </li>
          <li style={{ ...listResetStyles, padding: "2px 5px" }}>
            Prevent patients from selecting this HMO for billing or claims.
          </li>
        </ul>
        <Typography sx={{ fontWeight: 400, fontSize: "14px" }}>
          If you're sure you want to proceed, confirm below.
        </Typography>
      </Stack>

      <Stack spacing={1} direction={"row"} mt={3}>
        <Button
          onClick={() => mutate()}
          variant="contained"
          color="error"
          sx={{ fontWeight: "bold", width: "167px" }}
          disabled={isLoading}
        >
          Delete Provider
        </Button>
        <Button
          variant="text"
          onClick={toggleModal}
          sx={{ fontWeight: "bold", width: "167px" }}
        >
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};

const initialValues = {
  name: "",
  email: "",
  address: "",
};

const validate = (values) => {
  const errors = {};

  if (!values.name) {
    errors.name = "Required";
  }
  if (!values.email) {
    errors.email = "Required";
  }
  if (!values.address) {
    errors.address = "Required";
  }

  return errors;
};
const AddOrDelete = ({ hmo, toggleModal }) => {
  const queryClient = useQueryClient();

  const {
    handleBlur,
    handleChange,
    values,
    touched,
    errors,
    setValues,
    validateForm,
    setTouched,
    resetForm,
  } = useFormik({
    initialValues,
    validate: (values) => validate(values),
    onSubmit: handleSubmit,
  });

  useMemo(() => {
    if (hmo)
      setValues({ name: hmo.name, address: hmo.address, email: hmo.email });
  }, [hmo, setValues]);

  function handleSave(e) {
    e.preventDefault();
    setTouched({
      name: true,
      address: true,
      email: true,
    });

    validateForm().then((validationErrors) => {
      if (Object.keys(validationErrors).length === 0) {
        handleSubmit();
      }
    });
  }

  //Add Hmo
  const { mutate: addHmo, isLoading: addHmoLoading } = useCustomMutation(
    {
      url: `/hmo`,
      method: "post",
      data: values,
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(GET_HMOS);
        toast.success("Success");
        resetForm();
      },

      onError: (error) => toast.error(error.message),
    }
  );

  //Edit Hmo
  const { mutate: editHmo, isLoading: editHmoLoading } = useCustomMutation(
    {
      url: `/hmo/${hmo?.id}`,
      method: "patch",
      data: { name: values.name, email: values.email, address: values.address },
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(GET_HMOS);
        toast.success("Success");
        toggleModal();
      },

      onError: (error) => toast.error(error.message),
    }
  );

  function handleSubmit() {
    hmo ? editHmo() : addHmo();
  }

  return (
    <Stack spacing={4}>
      <Typography sx={{ fontWeight: 800, fontSize: "21px" }}>
        {hmo ? "Edit HMO Details" : "Add HMO"}
      </Typography>
      <form onSubmit={handleSave}>
        <Stack spacing={1}>
          <CustomTextInput
            title="HMO Name"
            value={values.name}
            name="name"
            placeholder={"Enter HMO Name"}
            handleChange={handleChange}
            boxSx={{ width: "100%" }}
            {...handleErrorProps(touched.name, errors.name)}
            onBlur={handleBlur}
          />
          <CustomTextInput
            title="Office address"
            value={values.address}
            name="address"
            placeholder={"Enter contact address"}
            handleChange={handleChange}
            boxSx={{ width: "100%" }}
            {...handleErrorProps(touched.address, errors.address)}
            onBlur={handleBlur}
          />
          <CustomTextInput
            title="Contact Email"
            value={values.email}
            name="email"
            placeholder={"Enter contact email"}
            handleChange={handleChange}
            boxSx={{ width: "100%" }}
            {...handleErrorProps(touched.email, errors.email)}
            onBlur={handleBlur}
            type="email"
          />
        </Stack>
        <Stack spacing={1} direction={"row"} mt={3}>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            sx={{ fontWeight: "bold", width: "167px" }}
            disabled={addHmoLoading || editHmoLoading}
          >
            {hmo ? "Save Changes" : "Save"}
          </Button>
        </Stack>
      </form>
    </Stack>
  );
};

function HmoListModals({ hmo, modalType, toggleModal }) {
  let view = <></>;
  switch (modalType) {
    case "ADD":
      view = <AddOrDelete />;
      break;
    case "EDIT":
      view = <AddOrDelete hmo={hmo} toggleModal={toggleModal} />;
      break;
    case "DELETE":
      view = <DeleteModal hmo={hmo} toggleModal={toggleModal} />;
      break;

    default:
      break;
  }
  return <>{view}</>;
}

export default HmoListModals;
